/**
 * Open problems as Needs-you rows on the Overview, the one list the person reads first. Critical
 * and warning problems join it; info problems stay on the project. A problem that repeats a row
 * already there is said once (model.ts, dedupeWithNeeds). Pure, so tested.
 */
import type { Checkout, Id, Problem } from '@orgus/protocol';
import { dedupeWithNeeds, problemSource, sortProblems } from './model.ts';
import type { NeedSubject } from '../overview/needsYou.ts';

export interface ProblemNeedItem {
  key: string;
  kind: 'problem-critical' | 'problem-warning';
  title: string;
  detail: string;
  /** The source: the project's name, or Orgus. */
  where: string;
  projectId?: null;
  at: number;
  to: { problem: string };
  verb: string;
  problem: Problem;
  /** What it is about, so an Orgus Watch incident about the same thing is said once (watch/needs.ts). */
  subject?: NeedSubject;
}

/** The problem's subject in Needs you's terms, for the kinds an incident can repeat. */
function subjectOf(p: Problem): NeedSubject | undefined {
  const s = p.subject;
  return s && (s.kind === 'session' || s.kind === 'worktree' || s.kind === 'machine') ? { kind: s.kind, id: s.id } : undefined;
}

export function problemNeed(p: Problem, projectName: (id: Id) => string | undefined): ProblemNeedItem {
  return {
    key: `pp:${p.key}`,
    kind: p.severity === 'critical' ? 'problem-critical' : 'problem-warning',
    title: p.what,
    detail: p.why,
    where: problemSource(p, projectName),
    projectId: null,
    at: p.since,
    to: { problem: p.key },
    verb: 'Review',
    problem: p,
    ...(subjectOf(p) ? { subject: subjectOf(p) } : {}),
  };
}

/** The Needs-you rows without the ones a problem repeats, and the problems split by severity. */
export function problemNeeds<N extends { key: string }>(
  needs: readonly N[],
  problems: readonly Problem[],
  checkouts: readonly Pick<Checkout, 'id' | 'path' | 'machineId'>[],
  projectName: (id: Id) => string | undefined,
): { core: N[]; critical: ProblemNeedItem[]; warning: ProblemNeedItem[] } {
  const open = sortProblems(problems.filter((p) => p.severity !== 'info'));
  const d = dedupeWithNeeds(needs, open, checkouts);
  const rows = d.problems.map((p) => problemNeed(p, projectName));
  return { core: d.needs, critical: rows.filter((r) => r.kind === 'problem-critical'), warning: rows.filter((r) => r.kind === 'problem-warning') };
}
